import { useCallback, useEffect, useState } from 'react'
import {
  Autocomplete,
  Button,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowForward,
  DeleteOutline,
  ExpandLess,
  ExpandMore,
  Remove,
  ShoppingCartOutlined,
} from '@mui/icons-material'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'
import { ImpresSupplierOffer } from './impresTypes'
import { SUPPLIER_ORDER } from './ImpresCatalogFilters'
import { formatCurrency, ownerFilterQueryParam } from '../../helper'
import { quoteType } from '../../types'
import { useUser } from '../../hooks'
import { ImpresCartLine, useImpresCart } from './ImpresCartContext'
import { addCartItemsToQuote, buildSuccessMessage, undoAddedCartItems, AddedLineItem } from './impresCartSubmit'
import NewQuoteDialog from '../../NewQuote/NewQuote'

type CartRow = {
  id: string
  mfgPN: string
  supplierName: ImpresSupplierOffer['supplierName']
  supplierPartNumber: string | null
  unitPrice: number | null
  qty: number
}

function buildCartItems(cart: ReturnType<typeof useImpresCart>['cart']) {
  return Object.values(cart).flatMap(suppliers =>
    (Object.values(suppliers ?? {}) as (ImpresCartLine | undefined)[])
      .filter((line): line is ImpresCartLine => !!line && line.qty > 0)
      .map(line => ({ mfgPN: line.item.mfgPN, item: line.item, supplierName: line.supplierName, qty: line.qty })),
  )
}

function buildGroups(cart: ReturnType<typeof useImpresCart>['cart']) {
  const rowsBySupplier = new Map<ImpresSupplierOffer['supplierName'], CartRow[]>()
  for (const suppliers of Object.values(cart)) {
    for (const line of Object.values(suppliers ?? {}) as (ImpresCartLine | undefined)[]) {
      if (!line || line.qty <= 0) continue
      const supplierOffer = line.item.suppliers.find(s => s.supplierName === line.supplierName)
      const rows = rowsBySupplier.get(line.supplierName) ?? []
      rows.push({
        id: line.item.id,
        mfgPN: line.item.mfgPN,
        supplierName: line.supplierName,
        supplierPartNumber: supplierOffer?.supplierPartNumber ?? null,
        unitPrice: supplierOffer?.unitPrice ?? null,
        qty: line.qty,
      })
      rowsBySupplier.set(line.supplierName, rows)
    }
  }
  return SUPPLIER_ORDER.filter(name => rowsBySupplier.has(name)).map(name => ({
    supplierName: name,
    rows: rowsBySupplier.get(name) as CartRow[],
  }))
}

export default function ImpresCartDrawer() {
  const user = useUser()
  const { cart, setQty, clearCart, itemCount, drawerOpen, closeDrawer } = useImpresCart()
  const groups = buildGroups(cart)
  const subtotal = groups.reduce((sum, g) => sum + g.rows.reduce((s, r) => s + (r.unitPrice ?? 0) * r.qty, 0), 0)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<ImpresSupplierOffer['supplierName']>>(new Set())

  const [quotes, setQuotes] = useState<quoteType[]>([])
  const [selectedQuote, setSelectedQuote] = useState<quoteType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string[] | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [addedQuoteId, setAddedQuoteId] = useState<string | null>(null)
  const [addedLineItems, setAddedLineItems] = useState<AddedLineItem[]>([])
  const [newQuoteOpen, setNewQuoteOpen] = useState(false)

  const fetchQuotes = useCallback(async (): Promise<quoteType[]> => {
    if (!user.email) return []
    try {
      const res = await axios.get(
        '/quote?quoteNumber=&crmAccount=&crmContact=&crmOpportunity=&quoteStatus=&page=0&pageSize=1000' +
          '&sort=CreatedDate&sortDirection=desc' +
          ownerFilterQueryParam(user.email, user.role),
      )
      setQuotes(res.data.list)
      return res.data.list
    } catch (err) {
      console.error('Failed to load quotes', err)
      return []
    }
  }, [user.email, user.role.admin, user.role.operations])

  useEffect(() => {
    if (!drawerOpen) return
    fetchQuotes()
  }, [drawerOpen, fetchQuotes])

  useEffect(() => {
    if (drawerOpen) return
    setSelectedQuote(null)
    setSubmitError(null)
  }, [drawerOpen])

  const changeQty = (row: CartRow, qty: number) => {
    const line = cart[row.id]?.[row.supplierName]
    if (line) setQty(line.item, row.supplierName, qty)
  }

  const toggleGroup = (supplierName: ImpresSupplierOffer['supplierName']) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(supplierName)) next.delete(supplierName)
      else next.add(supplierName)
      return next
    })
  }

  const addToQuote = async (quoteId: string) => {
    const cartItems = buildCartItems(cart)
    if (cartItems.length === 0) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const { added, failed } = await addCartItemsToQuote(quoteId, cartItems)
      if (added.length > 0) {
        setAddedQuoteId(quoteId)
        setAddedLineItems(added)
        setSuccessMessage(buildSuccessMessage(added.length, cartItems.length))
      }
      if (failed.length === 0) {
        clearCart()
      } else {
        setSubmitError(failed)
      }
    } catch (err) {
      console.error('Failed to load quote before adding line items', err)
      setSubmitError(cartItems.map(c => `${c.mfgPN} (${c.supplierName})`))
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddToQuote = async () => {
    if (!selectedQuote) return
    await addToQuote(selectedQuote.QuoteId)
  }

  const handleQuoteCreated = async (quoteId: string) => {
    const [quoteRes] = await Promise.all([
      axios.get(`/quote/${quoteId}`).catch(err => {
        console.error('Failed to load newly created quote', err)
        return null
      }),
      fetchQuotes(),
    ])
    if (quoteRes) setSelectedQuote(quoteRes.data.quote)
  }

  const handleUndo = async () => {
    if (!addedQuoteId) return
    const remaining = await undoAddedCartItems(addedQuoteId, addedLineItems)
    setAddedLineItems(remaining)
    if (remaining.length > 0) {
      setSuccessMessage(`Failed to undo ${remaining.length} item(s). Click Undo to retry.`)
    } else {
      setSuccessMessage(null)
      setAddedQuoteId(null)
    }
  }

  return (
    <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
      <div className="impres-cart-drawer">
        <div className="impres-cart-header">
          <Typography variant="h5">IMPRES Catalog Cart</Typography>
          <IconButton onClick={closeDrawer} aria-label="Close cart">
            <ArrowForward />
          </IconButton>
        </div>
        <Divider />
        <div className="impres-cart-body">
          {itemCount === 0 ? (
            <Typography className="impres-cart-empty">Your cart is empty</Typography>
          ) : (
            groups.map(group => {
              const isCollapsed = collapsedGroups.has(group.supplierName)
              return (
                <div key={group.supplierName}>
                  <div className="impres-cart-supplier-header">
                    <Typography variant="h6">{group.supplierName}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => toggleGroup(group.supplierName)}
                      aria-label={isCollapsed ? `Expand ${group.supplierName}` : `Collapse ${group.supplierName}`}
                    >
                      {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                    </IconButton>
                  </div>
                  {!isCollapsed &&
                    group.rows.map((row, index) => (
                      <div key={`${row.id}-${row.supplierName}`}>
                        <div className="impres-cart-row">
                          <div className="impres-cart-row-left">
                            <Typography>{row.mfgPN}</Typography>
                            <Typography variant="body2" className="impres-info-label">
                              Supplier part: {row.supplierPartNumber || '—'}
                            </Typography>
                          </div>
                          <div className="impres-cart-row-right">
                            <Typography color="text.secondary" className="impres-supplier-price">
                              {row.unitPrice === null ? '—' : formatCurrency(row.unitPrice)}
                            </Typography>
                            <div className="impres-qty-stepper">
                              <IconButton
                                size="small"
                                disabled={row.qty <= 0}
                                onClick={() => changeQty(row, Math.max(0, row.qty - 1))}
                                aria-label={`Decrease ${row.supplierName} quantity for ${row.mfgPN}`}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                value={row.qty}
                                onChange={e => {
                                  const parsed = parseInt(e.target.value, 10)
                                  changeQty(row, isNaN(parsed) ? 0 : Math.max(0, parsed))
                                }}
                                slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'center' } } }}
                                className="impres-qty-input"
                              />
                              <IconButton
                                size="small"
                                onClick={() => changeQty(row, row.qty + 1)}
                                aria-label={`Increase ${row.supplierName} quantity for ${row.mfgPN}`}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </div>
                            <Typography variant="h6" className="impres-supplier-total">
                              {row.unitPrice === null ? '—' : formatCurrency(row.unitPrice * row.qty)}
                            </Typography>
                            <IconButton
                              onClick={() => changeQty(row, 0)}
                              aria-label={`Remove ${row.mfgPN} (${row.supplierName}) from cart`}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </div>
                        </div>
                        {index < group.rows.length - 1 && <Divider />}
                      </div>
                    ))}
                  <Divider />
                </div>
              )
            })
          )}
        </div>
        <div className="impres-cart-footer">
          <div className="impres-cart-footer-subtotal">
            <Typography>Subtotal ({itemCount} Items):</Typography>
            <Typography variant="h6">{formatCurrency(subtotal)}</Typography>
          </div>
          <div className="impres-cart-footer-actions">
            <Autocomplete
              className="impres-cart-quote-select"
              options={quotes}
              getOptionLabel={q => `${q.QuoteNumber}v${q.Version}${q.Name ? ' - ' + q.Name : ''}`}
              isOptionEqualToValue={(a, b) => a.QuoteId === b.QuoteId}
              value={selectedQuote}
              onChange={(_e, value) => setSelectedQuote(value)}
              disabled={submitting}
              renderInput={params => <TextField {...params} label="IMPRES Quote" size="small" />}
            />
            <div className="impres-cart-footer-buttons">
              <Button variant="outlined" onClick={() => setNewQuoteOpen(true)} disabled={submitting}>
                Create New Quote
              </Button>
              <Button
                variant="contained"
                startIcon={<ShoppingCartOutlined />}
                onClick={handleAddToQuote}
                disabled={!selectedQuote || itemCount === 0 || submitting}
                loading={submitting}
              >
                Add To Quote
              </Button>
            </div>
          </div>
          {submitError && (
            <Typography color="error" variant="body2" className="impres-cart-submit-error">
              Failed to add: {submitError.join(', ')}. The other items above were already added to the quote — lower
              their quantity to 0 before retrying, or you'll add them again.
            </Typography>
          )}
        </div>
      </div>
      {newQuoteOpen && <NewQuoteDialog open onClose={() => setNewQuoteOpen(false)} onCreated={handleQuoteCreated} />}
      <Snackbar
        autoHideDuration={10000}
        open={successMessage !== null}
        onClose={() => setSuccessMessage(null)}
        message={
          <>
            {successMessage}
            {addedQuoteId && (
              <>
                {' '}
                <MuiLink
                  component={RouterLink}
                  to={`/quote/${addedQuoteId}`}
                  onClick={() => setSuccessMessage(null)}
                  color="#ffffff"
                >
                  View Quote
                </MuiLink>
              </>
            )}
          </>
        }
        action={
          <Button size="small" onClick={handleUndo} color="inherit">
            Undo
          </Button>
        }
      />
    </Drawer>
  )
}
